import React, { useEffect, useState } from 'react'

import { Card, Paper, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'

import { BuildingBlocksPromptResponse } from 'models/ideaModels'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import refreshIcon from 'assets/images/refreshIcon.svg'

import { makeStyles } from './Section2.styles'

export const TEST_ID = 'building-blocks-section2'

export type Props = {
	additionalContextResponse?: string
	buildingBlocksPrompt?: BuildingBlocksPromptResponse
	createPrompt: () => void
	isAdditionalContextLoading: boolean
	loadAdditionalContext: () => Promise<void>
	loading: boolean
	reloadIdeas: () => Promise<boolean>
}

const Section2 = ({ buildingBlocksPrompt, ...props }: Props) => {
	const styles = makeStyles(useTheme())
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)
	const [selectedSolutionIndex, setSelectedSolutionIndex] = useState(0)

	const recommendedSolutions: string[] = []
	const data: unknown = buildingBlocksPrompt?.data

	if (data) {
		if (typeof data === 'string') {
			recommendedSolutions.push(...data.trim().split('\n\n'))
		} else {
			// Including console warning just in case the API response changes
			// eslint-disable-next-line no-console, no-restricted-syntax
			console.warn(
				`Unexpected data type for buildingBlocksPrompt: ${typeof data}`
			)
		}
	}

	useEffect(() => {
		setSelectedSolutionIndex(0)
		if (recommendedSolutions.length && !props.isAdditionalContextLoading) {
			props.loadAdditionalContext()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buildingBlocksPrompt?.data])

	const onClickRefresh = () => {
		props.createPrompt()
	}

	const buildAdditionalContextResponse = () => {
		if (!props.additionalContextResponse) return undefined

		const sections = props.additionalContextResponse
			.split(/(?=\s*Concept Iterations?:)/i)
			.map((subsection) => subsection.trim())
			.filter(Boolean)

		const section = sections[selectedSolutionIndex]
		if (!section) return undefined

		const iterationsMatch = section.match(
			/Concept Iterations?:\s*([\s\S]*)/i
		)

		const iterationsText = iterationsMatch?.[1]?.trim() ?? ''

		const iterations = iterationsText
			.split('\n')
			.map((item) => item.trim())
			.filter(Boolean)

		if (!iterations.length) {
			return (
				<Typography component="p" variant="body1">
					{section}
				</Typography>
			)
		}

		return (
			<Box
				sx={styles.solutionBox}
				data-testid={`${TEST_ID}__selected-solution-context`}
			>
				<Typography
					component="p"
					variant="overline"
					sx={styles.solutionTextStyle}
					data-testid={`${TEST_ID}__concept-iterations-heading`}
				>
					Concept Iterations:
				</Typography>
				{iterations.map((iteration, index) => (
					<Typography
						component="p"
						key={`concept-iteration-${index}`}
						variant="body1"
						sx={styles.solutionTextStyle}
						data-testid={`${TEST_ID}__concept-iteration-item`}
						gutterBottom
					>
						- {iteration}
					</Typography>
				))}
			</Box>
		)
	}

	return (
		<>
			<Section2Title showInfoPopper={false} />
			<UserEducationModal />
			<Box sx={styles.mainBox}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.typographyBox}>
					<Typography
						sx={styles.typography}
						data-testid={`${TEST_ID}-user-directions-verbiage`}
					>
						Consider these AI-generated solutions to the innovation
						topic.
					</Typography>
					<Box sx={styles.refreshButtonBox}>
						<ButtonWithIconComponent
							disabled={props.loading}
							iconSvg={refreshIcon}
							text="REFRESH"
							data-testid={`${TEST_ID}__refresh-button`}
							onClickAction={onClickRefresh}
						/>
					</Box>
					{!buildingBlocksPrompt || props.loading ? (
						<LoadingSpinner />
					) : (
						<Paper
							elevation={0}
							id="recommended-solutions-paper"
							sx={styles.recommendedSolutionsPaper}
						>
							{recommendedSolutions.map((solution, index) => (
								<Card
									elevation={0}
									id={`recommended-solution-${index}`}
									key={`recommended-solution-${index}`}
									sx={
										index === selectedSolutionIndex
											? styles.recommendedSolutionsCardSelected
											: styles.recommendedSolutionsCard
									}
									data-testid={`${TEST_ID}__recommended-solution-card`}
									onClick={() =>
										setSelectedSolutionIndex(index)
									}
								>
									<Typography
										sx={{
											...styles.typography,
											...styles.techniqueBox,
										}}
									>
										{solution}
									</Typography>
								</Card>
							))}
						</Paper>
					)}
					<GeneratedExampleDropdown
						gameTypeEnum={GAME_TYPE.BUILDING_BLOCKS}
						headerText={''}
						isLoading={
							props.loading || props.isAdditionalContextLoading
						}
						customResponse={buildAdditionalContextResponse()}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}
export default Section2
